import { useEffect, useRef } from 'react'
import { Mic, MicOff, PhoneOff, Loader2, NotebookPen, Languages, MessageSquare } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useConsoleState } from '@/lib/voice-console/state/context'

/** Typing-bubble icon — used on the swap button when in voice mode (→ switch to chat) */
function ChatBubbleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <circle cx="9" cy="10" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="10" r="1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="10" r="1" fill="currentColor" stroke="none" />
    </svg>
  )
}

/** Audio-waveform icon — used on the swap button when in chat mode (→ switch to voice) */
function WaveformIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="4"  y1="15" x2="4"  y2="9" />
      <line x1="8"  y1="18" x2="8"  y2="6" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="16" y1="18" x2="16" y2="6" />
      <line x1="20" y1="15" x2="20" y2="9" />
    </svg>
  )
}

export function ControlBar() {
  const { state } = useConsoleState()
  const { isConnected, assistantStatus, controls, isMicMuted, agentMode, connectionMode } = state

  // Volume ring refs — driven by rAF, no React state to avoid re-renders
  const ringRef = useRef<HTMLSpanElement>(null)
  const volumeSmoothRef = useRef(0)
  const rafRef = useRef<number>()

  useEffect(() => {
    const ring = ringRef.current
    if (!ring) return

    const active = isConnected && connectionMode === 'voice' && !isMicMuted && !!controls?.getInputVolume

    if (!active) {
      ring.style.opacity = '0'
      ring.style.transform = 'scale(1)'
      volumeSmoothRef.current = 0
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      return
    }

    const tick = () => {
      const raw = controls!.getInputVolume()
      volumeSmoothRef.current += (raw - volumeSmoothRef.current) * 0.25
      const v = volumeSmoothRef.current
      //ring.style.transform = `scale(${1 + v * 0.55})`
      //ring.style.opacity = String(Math.min(1, v * 4))
      ring.style.transform = `scale(${0.7 + v * 2})`
      ring.style.opacity = String(Math.min(1, v * 10))
      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [isConnected, connectionMode, isMicMuted, controls])

  if (!controls) {
    return (
      <div className="flex items-center justify-center h-16 border-t border-slate-800 bg-slate-950/80 shrink-0">
        <span className="text-xs text-slate-700">AI OS Console</span>
      </div>
    )
  }

  const isConnecting = assistantStatus === 'connecting'
  const iconButtonBase =
    'relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-all duration-200 sm:h-11 sm:w-11'
  const iconButtonSurface =
    'border-slate-700/80 bg-[radial-gradient(circle_at_top,rgba(30,41,59,0.98),rgba(2,6,23,0.92))] text-slate-300 shadow-[inset_0_1px_0_rgba(148,163,184,0.08),0_10px_24px_rgba(2,6,23,0.34)]'
  const iconButtonDisabled =
    'cursor-not-allowed border-slate-800/80 bg-slate-950/80 text-slate-600 opacity-50 shadow-none'
  const segmentedButtonBase =
    'flex h-full min-w-0 flex-1 items-center justify-center gap-2 rounded-[0.95rem] px-4 text-[13px] font-semibold tracking-[0.01em] transition-all duration-200 sm:px-5 sm:text-sm'

  return (
    <div className="sticky bottom-0 z-20 shrink-0 border-t border-slate-800/80 bg-[linear-gradient(180deg,rgba(2,6,23,0.9),rgba(2,6,23,0.98))] px-3 py-2.5 backdrop-blur-xl sm:px-4 sm:py-3">
      <div className="flex items-center gap-2 sm:gap-3">

        {/* Left: Swap mode + Mic toggle */}
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {/* Swap mode button — bidirectional chat ↔ voice */}
          <button
            onClick={() => controls.switchMode(connectionMode === 'chat' ? 'voice' : 'chat')}
            disabled={!isConnected}
            className={cn(
              iconButtonBase,
              !isConnected
                ? iconButtonDisabled
                : cn(
                    iconButtonSurface,
                    connectionMode === 'chat'
                      ? 'hover:border-indigo-400/35 hover:bg-indigo-500/12 hover:text-indigo-100'
                      : 'hover:border-cyan-400/35 hover:bg-cyan-500/12 hover:text-cyan-100'
                  )
            )}
            title={connectionMode === 'chat' ? 'Switch to voice' : 'Switch to chat'}
          >
            {connectionMode === 'chat'
              ? <WaveformIcon className="w-5 h-5" />
              : <ChatBubbleIcon className="w-5 h-5" />
            }
          </button>

          {/* Mic mute toggle (mute/unmute only — mode switching handled by swap button) */}
          <div className="relative flex items-center justify-center">
            {/* Volume ring — animated by rAF directly */}
            <span
              ref={ringRef}
              className="absolute -inset-1 rounded-full border-2 border-cyan-400 pointer-events-none"
              style={{ opacity: 0, willChange: 'transform, opacity' }}
            />
            <button
              onClick={controls.toggleMic}
              disabled={!isConnected || connectionMode === 'chat'}
              className={cn(
                iconButtonBase,
                !isConnected || connectionMode === 'chat'
                  ? iconButtonDisabled
                  : isMicMuted
                    ? 'border-red-400/35 bg-red-500/14 text-red-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_0_18px_rgba(239,68,68,0.14)] hover:border-red-300/45 hover:bg-red-500/18'
                    : cn(
                        iconButtonSurface,
                        'hover:border-cyan-400/35 hover:bg-cyan-500/10 hover:text-slate-50'
                      )
              )}
              title={connectionMode === 'chat' ? 'Mic inactive in chat mode' : isMicMuted ? 'Unmute mic' : 'Mute mic'}
            >
              {isMicMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Center: Split connect pill (disconnected) or End button (connected) */}
        <div className="flex min-w-0 flex-1 justify-center">
          {isConnected ? (
            <button
              onClick={controls.disconnect}
              className="flex h-11 w-full max-w-[232px] items-center justify-center gap-2 rounded-[1.15rem] border border-red-400/25 bg-[linear-gradient(180deg,rgba(190,24,93,0.92),rgba(153,27,27,0.96))] px-4 text-sm font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_14px_30px_rgba(127,29,29,0.36)] transition-all duration-200 hover:border-red-300/30 hover:brightness-110 sm:h-12 sm:px-5"
            >
              <PhoneOff className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="whitespace-nowrap">{connectionMode === 'chat' ? 'End Chat' : 'End Call'}</span>
            </button>
          ) : isConnecting ? (
            <button
              disabled
              className="flex h-11 w-full max-w-[232px] cursor-not-allowed items-center justify-center gap-2 rounded-[1.15rem] border border-slate-700/80 bg-[linear-gradient(180deg,rgba(15,23,42,0.96),rgba(2,6,23,0.98))] px-4 text-sm font-semibold text-slate-400 shadow-[inset_0_1px_0_rgba(148,163,184,0.06),0_12px_24px_rgba(2,6,23,0.26)] sm:h-12 sm:px-5"
            >
              <Loader2 className="h-4 w-4 animate-spin sm:h-5 sm:w-5" />
              <span className="whitespace-nowrap">Connecting</span>
            </button>
          ) : (
            <div className="flex h-11 w-full max-w-[232px] min-w-0 items-center rounded-[1.2rem] border border-slate-700/80 bg-[linear-gradient(180deg,rgba(15,23,42,0.96),rgba(2,6,23,0.98))] p-1 shadow-[inset_0_1px_0_rgba(148,163,184,0.08),0_14px_30px_rgba(2,6,23,0.4)] sm:h-12 sm:max-w-[248px]">
              <button
                onClick={() => controls.connect('chat')}
                className={cn(
                  segmentedButtonBase,
                  'border border-cyan-400/20 bg-cyan-500/14 text-cyan-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] hover:border-cyan-300/30 hover:bg-cyan-400/20 hover:text-white'
                )}
              >
                <MessageSquare className="h-4 w-4 shrink-0 text-cyan-300" />
                <span className="whitespace-nowrap">Chat</span>
              </button>
              <button
                onClick={() => controls.connect('voice')}
                className={cn(
                  segmentedButtonBase,
                  'border border-indigo-400/20 bg-indigo-500/16 text-indigo-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] hover:border-indigo-300/45 hover:bg-indigo-400/30 hover:text-white hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_0_18px_rgba(99,102,241,0.18)]'
                )}
              >
                <Mic className="h-4 w-4 shrink-0 text-indigo-300" />
                <span className="whitespace-nowrap">Voice</span>
              </button>
            </div>
          )}
        </div>

        {/* Right: Mode buttons */}
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <button
            onClick={controls.toggleMeeting}
            disabled={!isConnected || agentMode === 'translator'}
            className={cn(
              iconButtonBase,
              !isConnected || agentMode === 'translator' ? iconButtonDisabled :
              agentMode === 'meeting'
                ? 'border-amber-400/35 bg-amber-500/14 text-amber-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_0_18px_rgba(245,158,11,0.16)]'
                : cn(
                    iconButtonSurface,
                    'hover:border-amber-400/35 hover:bg-amber-500/10 hover:text-amber-100'
                  )
            )}
            title="Meeting mode"
          >
            <NotebookPen className="h-5 w-5" />
          </button>

          <button
            onClick={controls.toggleTranslator}
            disabled={agentMode === 'meeting'}
            className={cn(
              iconButtonBase,
              agentMode === 'meeting' ? iconButtonDisabled :
              agentMode === 'translator'
                ? 'border-sky-400/35 bg-sky-500/14 text-sky-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_0_18px_rgba(56,189,248,0.14)]'
                : cn(
                    iconButtonSurface,
                    'hover:border-sky-400/35 hover:bg-sky-500/10 hover:text-sky-100'
                  )
            )}
            title="Translator mode"
          >
            <Languages className="h-5 w-5" />
          </button>
        </div>

      </div>
    </div>
  )
}
